import React, { useState } from 'react';
import { Button, Menu, MenuItem, FormControlLabel, Radio, Box, Divider } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useTranslate } from 'src/locales';

interface ByzViewModeSelectorProps {
    viewMode: 'list' | 'card';
    onChange: (mode: 'list' | 'card') => void;
    sx?: any;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
}

export default function ByzViewModeSelector({
    viewMode,
    onChange,
    sx,
    startIcon = <img src="/assets/icons/pages/nurse/kanban-filled.svg" alt="" style={{ width: 20, height: 20 }} />,
    endIcon = <Iconify icon="eva:arrow-ios-downward-fill" />
}: ByzViewModeSelectorProps) {
    const { t } = useTranslate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (mode: 'list' | 'card') => {
        onChange(mode);
        handleClose();
    };

    return (
        <Box sx={sx}>
            <Button
                id="view-mode-button"
                aria-controls={open ? 'view-mode-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                startIcon={startIcon}
                endIcon={endIcon}
                sx={{
                    color: '#2498f3',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    minWidth: 140,
                    height: '36px', // Aligned with other filter components
                    justifyContent: 'space-between',
                    px: 1.5,
                    border: '1px solid #2498f3',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': {
                        backgroundColor: 'rgba(36, 152, 243, 0.04)',
                        borderColor: '#2498f3',
                    }
                }}
            >
                <Box component="span" sx={{ flexGrow: 1, textAlign: 'left', ml: 1 }}>
                    {t("common.view")}
                </Box>
            </Button>
            <Menu
                id="view-mode-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'view-mode-button',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 160,
                            boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.1)',
                            borderRadius: '12px',
                            mt: 0.5
                        }
                    }
                }}
            >
                <MenuItem onClick={() => handleSelect('card')}>
                    <FormControlLabel
                        control={
                            <Radio
                                checked={viewMode === 'card'}
                                size="small"
                                icon={<Iconify icon="eva:radio-button-off-outline" />}
                                checkedIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                            />
                        }
                        label={t("common.card")}
                        sx={{ pointerEvents: 'none', mr: 0 }}
                    />
                </MenuItem>
                <MenuItem onClick={() => handleSelect('list')}>
                    <FormControlLabel
                        control={
                            <Radio
                                checked={viewMode === 'list'}
                                size="small"
                                icon={<Iconify icon="eva:radio-button-off-outline" />}
                                checkedIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                            />
                        }
                        label={t("common.list")}
                        sx={{ pointerEvents: 'none', mr: 0 }}
                    />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleClose} sx={{ color: 'primary.main', justifyContent: 'center' }}>
                    {t("common.close")}
                </MenuItem>
            </Menu>
        </Box>
    );
}
